import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { theme } from 'antd';
import additionalInfoSaveKeys from 'constants/additionalInfoSaveKeys';
import Helpers from 'utils/Helpers';
import { RootState } from 'store/configureStore';
import { gapsOp } from 'store/ducks/Gaps';
import { IAdditionalInfo, IAudits } from 'store/ducks/Gaps/types';
import { globalOp } from 'store/ducks/Global';
import { complianceEstOp } from 'store/ducks/ComplienceEst';
import { GapAdditionalInfoColumns } from 'constants/tableColumns';
import * as Styled from './AdditionalInfoTable.styled';

const { useToken } = theme;

export interface IAdditionalInfoTableRow extends IAdditionalInfo {
  key: string;
  audit?: IAudits;
  gapRowSpan: number;
}

const AdditionalInfoTable: React.FC = React.memo(() => {
  const dispatch = useDispatch();
  const { token } = useToken();

  const [prioritization, setPrioritization] = useState<string[]>([]);
  const [gapSource, setGapSource] = useState<string[]>([]);
  const [ownersList, setOwnersList] = useState<string[]>([]);

  const { lang, additionalInfo, saveClicked, infoSave, loading } = useSelector(
    (state: RootState) => ({
      lang: state.global.lang,
      loading: state.gaps.loading,
      additionalInfo: state.gaps.additionalInfo,
      saveClicked: state.complianceEst.saveClicked,
      infoSave: state.gaps.infoSave
    })
  );

  const handleSelectChange = (
    value: string,
    type: string,
    data: IAdditionalInfo
  ) => {
    const updatedData = () => {
      const newState = { ...infoSave };
      const gapId = data.gap_id as number;

      // Use existing saved data if available, otherwise use original data
      const existingData = newState[gapId] || {};

      const newData = additionalInfoSaveKeys.reduce((obj, item) => {
        const existingValue = existingData[item as keyof typeof existingData];
        const originalValue = data[item as keyof typeof data];
        return Object.assign(obj, {
          [item]: existingValue !== undefined ? existingValue : originalValue
        });
      }, {});

      newState[gapId] = { ...newData, [type]: value };
      return newState;
    };

    dispatch(gapsOp.setInfoSaveData(updatedData()));
  };

  const gapAdditionalInfoColumns = GapAdditionalInfoColumns({
    prioritization,
    gapSource,
    ownersList,
    handleSelectChange
  });

  useEffect(() => {
    if (!Helpers.isObjEmpty(infoSave) && saveClicked) {
      // Execute save immediately without delay
      gapsOp
        .saveGapsInfo(Object.values(infoSave), lang)
        .then(() => {
          dispatch(complianceEstOp.setSaveClicked(false));
          dispatch(gapsOp.setInfoSaveData({}));
        })
        .catch((error) => {
          dispatch(complianceEstOp.setSaveClicked(false));
        });
    }
  }, [saveClicked, infoSave, lang, dispatch]);

  const buildRows = (data: IAdditionalInfo[]): IAdditionalInfoTableRow[] => {
    const rows: IAdditionalInfoTableRow[] = [];

    data.forEach((gap: IAdditionalInfo) => {
      if (gap.AUDITS?.length) {
        gap.AUDITS.forEach((audit: IAudits, index: number) => {
          rows.push({
            ...gap,
            audit,
            key: `${gap.gap_id}-${audit.afg_id ?? index}`,
            gapRowSpan: index === 0 ? gap.AUDITS.length : 0
          });
        });
      } else {
        rows.push({
          ...gap,
          audit: undefined,
          key: `${gap.gap_id}`,
          gapRowSpan: 1
        });
      }
    });

    return rows;
  };

  const dataSource = buildRows(
    (additionalInfo?.flat() ?? []) as IAdditionalInfo[]
  );

  useEffect(() => {
    const isEmpty = Helpers.isObjEmpty(infoSave);
    dispatch(complianceEstOp.setSaveDisabled(isEmpty));
  }, [infoSave, dispatch]);

  useEffect(() => {
    gapsOp.fetchPrioritization(lang).then((response) => {
      const sorted = [...(response?.prioritization || [])].sort((a, b) =>
        a.localeCompare(b)
      );
      setPrioritization(sorted);
    });

    gapsOp.fetchGapSource(lang).then((response) => {
      const sorted = [...(response?.gap_sources || [])].sort((a, b) =>
        a.localeCompare(b)
      );
      setGapSource(sorted);
    });

    globalOp.fetchOwnersList().then((response: string[]) => {
      const sorted = [...(response || [])].sort((a, b) => a.localeCompare(b));
      setOwnersList(sorted);
    });
  }, []);

  return (
    <>
      <Styled.StyledCustomTable
        drawTable
        token={token}
        columns={gapAdditionalInfoColumns}
        dataSource={dataSource}
        itemsCount={dataSource.length}
        loading={loading}
      />
    </>
  );
});

export default AdditionalInfoTable;
